import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { DataTable, FAIcon, Input } from '@/components'
import { ALL_FACILITIES_QUERY } from '@/modules/companies/queries'
import type { FacilityNode } from '@/modules/companies/types.graphql'
import { useConfirm } from '@/utils/hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import type { FacilityForm } from './FacilityPrompt'
import { FacilityPrompt } from './FacilityPrompt'
import { useFacilityMutations } from './hooks'
import { getNodes } from '@/utils/graphql'
import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'

const column = createColumnHelper<FacilityNode>()

export const FacilitySettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.facility-delete-title',
    },
    externalId: 'common.id',
    facilities: 'common.facilities',
    name: 'common.name',
    noResults: 'common.no-results',
    search: 'common.search',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useFacilityMutations()

  const { data } = useQuery(ALL_FACILITIES_QUERY)

  const facilities = useMemo(() => getNodes(data?.allFacilities), [data])
  const [search, setSearch] = useState('')

  const filteredFacilities = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return facilities

    return facilities.filter(facility =>
      [facility.name, facility.externalId]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(normalizedSearch))
    )
  }, [facilities, search])

  async function handleCreateFacility() {
    const { data } = await addPrompt<FacilityForm | null>(resolve => (
      <FacilityPrompt existingFacilities={facilities} onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.createFacility({
      variables: {
        input: data,
      },
    })
  }

  const handlePatchFacility = useCallback(
    async (facility: FacilityNode) => {
      const { data } = await addPrompt<FacilityForm | null>(resolve => (
        <FacilityPrompt
          initialData={facility}
          existingFacilities={facilities}
          onSubmit={resolve}
        />
      ))
      if (data === null) return

      void mutations.patchFacility({
        variables: {
          id: facility.id,
          input: {
            externalId: data.externalId,
            name: data.name,
          },
        },
      })
    },
    [addPrompt, facilities, mutations]
  )

  const handleDeleteFacility = useCallback(
    async (facility: FacilityNode) => {
      const { data: answer } = await confirm(
        translations.prompt.delete({ name: facility.name }),
        translations.prompt.deleteTitle
      )
      if (!answer) return

      void mutations.deleteFacility({
        variables: {
          id: facility.id,
        },
      })
    },
    [confirm, mutations, translations.prompt]
  )

  const tableColumns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
      }),
      column.accessor('externalId', {
        header: translations.externalId,
        enableSorting: true,
        cell(info) {
          return <span>{info.getValue() ?? '-'}</span>
        },
      }),
      column.display({
        id: 'edit',
        meta: {
          narrow: true,
        },
        cell(info) {
          const facility = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-secondary"
              icon="edit"
              cursor="pointer"
              onClick={() => handlePatchFacility(facility)}
            />
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: {
          narrow: true,
        },
        cell(info) {
          const facility = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDeleteFacility(facility)}
            />
          )
        },
      }),
    ],
    [
      handleDeleteFacility,
      handlePatchFacility,
      translations.externalId,
      translations.name,
    ]
  )

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.facilities}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateFacility} />
      </Header>

      <div className="mb-4 max-w-sm">
        <Input
          aria-label={translations.search}
          placeholder={translations.search}
          type="search"
          value={search}
          onChange={setSearch}
        />
      </div>

      <DataTable
        columns={tableColumns}
        data={filteredFacilities}
        noDataText={translations.noResults}
        pagination={{ pageSize: 20 }}
      />
    </OldSection>
  )
}
